import React, { useState } from 'react';
import { Drawer, Descriptions, Tag, Divider, Typography, Tooltip, Button, Flex } from 'antd';
import { CopyOutlined, LinkOutlined, RedoOutlined, SendOutlined } from '@ant-design/icons';
import { DataType } from '@src/interfaces/costcoDashboard.interface';
import dayjs from 'dayjs';
import { useNotification } from '@src/providers/NotificationProviders';
import makeApiRequest from '@src/apiCalls/makeApiRequest';
import { API_ENDPOINTS } from '@src/constants/apiEndpoints';
import apiRequestConfig from '@src/interfaces/apiEndpoints.interface';

const { Text, Link } = Typography;

interface MembershipDetailsDrawerProps {
    open: boolean;
    onClose: () => void;
    data: DataType | null;
    verificationType?: string;
    onRefresh?: () => void;
}

const MembershipDetailsDrawer: React.FC<MembershipDetailsDrawerProps> = ({
    open,
    onClose,
    data,
    verificationType,
    onRefresh,
}) => {
    const [width, setWidth] = useState(600);
    const [isResizing, setIsResizing] = useState(false);

    const handleMouseDown = (e: React.MouseEvent) => {
        setIsResizing(true);
        e.preventDefault();
    };

    const handleMouseMove = React.useCallback(
        (e: MouseEvent) => {
            if (!isResizing) return;
            const newWidth = window.innerWidth - e.clientX;
            if (newWidth > 400 && newWidth < 1200) {
                setWidth(newWidth);
            }
        },
        [isResizing]
    );

    const handleMouseUp = React.useCallback(() => {
        setIsResizing(false);
    }, []);

    React.useEffect(() => {
        if (isResizing) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
        } else {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        }
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isResizing, handleMouseMove, handleMouseUp]);

    const openNotification = useNotification();
    const [loading, setLoading] = useState(false);
    const token = localStorage.getItem('costcoAdminToken');

    if (!data) return null;

    const formatDate = (value: any) => {
        if (!value || value === 'NA' || value === 0) return 'NA';
        if (typeof value === 'string' && !isNaN(Number(value))) {
            return dayjs.unix(Number(value)).format('MMMM D, YYYY h:mm A');
        }
        if (typeof value === 'number') {
            return dayjs.unix(value).format('MMMM D, YYYY h:mm A');
        }
        return value;
    };

    const renderValue = (label: string, value: any) => {
        if (label === 'notificationSent') {
            return value ? 'Yes' : 'No';
        }

        if (value === undefined || value === null || value === 'NA' || value === '') return 'NA';

        if (typeof value === 'boolean') {
            return value ? 'Yes' : 'No';
        }

        if (label.toLowerCase().includes('link')) {
            return (
                <Flex align="center" gap={8}>
                    <Tooltip title={value}>
                        <Link href={value} target="_blank" ellipsis style={{ maxWidth: 200 }}>
                            <LinkOutlined /> View Link
                        </Link>
                    </Tooltip>
                    <Text copyable={{ text: value }} />
                </Flex>
            );
        }

        if (label === 'status') {
            const isMilitary = verificationType === 'MILITARY';
            return (
                <Flex align="center" gap={16}>
                    <Tag
                        color={value === 'VERIFIED' ? 'green' : value === 'FAILED' ? 'red' : 'orange'}
                        style={{ margin: 0, fontWeight: 500 }}
                    >
                        {value}
                    </Tag>
                    {isMilitary && (
                        <Flex gap={16} align="center">
                            <Tooltip title={value === 'PENDING' ? 'Current status is already PENDING' : 'Reset status to PENDING'}>
                                <Button
                                    type="link"
                                    size="small"
                                    onClick={handleOverride}
                                    loading={loading}
                                    disabled={value === 'PENDING'}
                                    icon={<RedoOutlined />}
                                    style={{
                                        padding: 0,
                                        height: 'auto',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px'
                                    }}
                                >
                                    Reverify
                                </Button>
                            </Tooltip>
                            <Tooltip title={value !== 'PENDING' ? 'Can only resend verification link for PENDING status' : 'Resend verification link down to customer'}>
                                <Button
                                    type="link"
                                    size="small"
                                    onClick={handleResend}
                                    loading={loading}
                                    disabled={value !== 'PENDING'}
                                    icon={<SendOutlined />}
                                    style={{
                                        padding: 0,
                                        height: 'auto',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px'
                                    }}
                                >
                                    Resend
                                </Button>
                            </Tooltip>
                        </Flex>
                    )}
                </Flex>
            );
        }

        if (label === 'idpVerifiedEmail') {
            const displayValue = (value && value !== 'NA') ? value : 'NA';
            if (displayValue === 'NA') return 'NA';
            return (
                <Text
                    ellipsis={{ tooltip: displayValue }}
                    style={{ maxWidth: 300 }}
                    copyable={{ text: String(displayValue) }}
                >
                    {String(displayValue)}
                </Text>
            );
        }
        if (label.toLowerCase().includes('at') || label.toLowerCase().includes('time')) {
            return formatDate(value);
        }

        return (
            <Text
                ellipsis={{ tooltip: value }}
                style={{ maxWidth: 300 }}
                copyable={{ text: String(value) }}
            >
                {String(value)}
            </Text>
        );
    };

    const getLabel = (field: string) => {
        if (field === 'completedAt') return 'Verified At';
        if (field === 'customerEmail') return 'Customer Email';
        if (field === 'customerId') return 'Customer ID';
        if (field === 'idpVerifiedEmail') return 'Verified Email';
        if (field === 'membershipId' && (verificationType === 'CAA' || verificationType === 'AAA')) {
            return 'Membership Number';
        }
        if (field === 'membershipId') return 'Membership ID';
        return field.replace(/([A-Z])/g, ' $1')
            .replace(/^./, str => str.toUpperCase())
            .trim();
    };

    const internalFields = ['key', 'providerResponse'];
    const customerFields = ['customerId', 'customerEmail', 'customerPhone'];
    const verificationFields = Object.keys(data).filter(
        key => !internalFields.includes(key) && !customerFields.includes(key) && key !== 'verificationId'
    );



    const handleOverride = async () => {
        if (!data?.verificationId) return;
        setLoading(true);
        try {
            await makeApiRequest(
                {},
                API_ENDPOINTS.OVERRIDE_MILITARY_VERIFICATION as apiRequestConfig,
                data.verificationId,
                { Authorization: `Bearer ${token}` }
            );
            openNotification('Verification status overridden to PENDING successfully', 'Success');
            onRefresh?.();
            onClose();
        } catch (error: any) {
            openNotification(error?.message || 'Failed to override verification', 'Error');
        } finally {
            setLoading(false);
        }
    };

    const handleResend = async () => {
        if (!data?.verificationId) return;
        setLoading(true);
        try {
            await makeApiRequest(
                {},
                API_ENDPOINTS.RESEND_MILITARY_VERIFICATION as apiRequestConfig,
                data.verificationId,
                { Authorization: `Bearer ${token}` }
            );
            openNotification('Verification link resent successfully', 'Success');
            onRefresh?.();
            onClose();
        } catch (error: any) {
            openNotification(error?.message || 'Failed to resend verification', 'Error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Drawer
            title="Verification Details"
            placement="right"
            onClose={onClose}
            open={open}
            width={width}
            maskClosable={true}
            styles={{
                body: { padding: 0 }
            }}
        >
            <div
                onMouseDown={handleMouseDown}
                style={{
                    width: '6px',
                    height: '100%',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    cursor: 'ew-resize',
                    zIndex: 100,
                    backgroundColor: isResizing ? '#1890ff' : 'transparent',
                    transition: 'background-color 0.3s'
                }}
            />
            <div style={{ padding: '24px' }}>
                <Descriptions title="Customer Information" column={1} bordered>
                    {customerFields.map(field => {
                        const value = data[field as keyof DataType];
                        if (!value || value === 'NA') return null;
                        return (
                            <Descriptions.Item key={field} label={getLabel(field)}>
                                {renderValue(field, value)}
                            </Descriptions.Item>
                        );
                    })}
                </Descriptions>

                <Divider />

                <Descriptions title="Verification Details" column={1} bordered>
                    {verificationFields.map(field => {
                        const value = data[field as keyof DataType];
                        if (field === 'idpVerifiedEmail' && verificationType !== 'MILITARY') return null;
                        if (value === undefined || value === null || (value === 'NA' && field !== 'idpVerifiedEmail') || value === 0) return null;
                        return (
                            <Descriptions.Item key={field} label={getLabel(field)}>
                                {renderValue(field, value)}
                            </Descriptions.Item>
                        );
                    })}
                </Descriptions>

                {data.providerResponse && data.providerResponse !== 'NA' && (
                    <>
                        <Divider />
                        <Descriptions title="Provider Response" column={1} bordered>
                            <Descriptions.Item label="Raw Data">
                                <pre style={{
                                    whiteSpace: 'pre-wrap',
                                    fontSize: '11px',
                                    background: '#f5f5f5',
                                    padding: '10px',
                                    borderRadius: '4px',
                                    maxHeight: '300px',
                                    overflow: 'auto'
                                }}>
                                    {(() => {
                                        try {
                                            return JSON.stringify(JSON.parse(data.providerResponse), null, 2);
                                        } catch (e) {
                                            return data.providerResponse;
                                        }
                                    })()}
                                </pre>
                                <Text copyable={{ text: data.providerResponse }} style={{ marginTop: '8px', display: 'block' }}>
                                    Copy Raw Data
                                </Text>
                            </Descriptions.Item>
                        </Descriptions>
                    </>
                )}
            </div>
        </Drawer>
    );
};


export default MembershipDetailsDrawer;
